<script setup lang="ts">
    import { initValidators } from '@/services/validators/initialize';
    import TheToastersManager from '@/components/TheToastersManager.vue';
    import { useMaterialUpdater } from '@/composables/useMaterialUpdater';
    import TheLoader from '@/components/TheLoader.vue';
    import DesktopView from '@/views/DesktopView.vue';
    import MobileView from '@/views/MobileView.vue';
    import { throttle } from '@/utils/throttle';
    import useProductStore from '@/store/product.store';
    import useConfigStore from '@/store/config.store';
    import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
    import { Color, MeshStandardMaterial } from 'three';
    import { storeToRefs } from 'pinia';
    import { useI18n } from 'vue-i18n';
    import type { ITextField } from '@/typescript/interfaces/ITextField';
    import type { IImageField } from '@/typescript/interfaces/IImageField';

    const configStore = useConfigStore();

    const { isMobile, isAppLoading } = storeToRefs(configStore);
    const { fonts, uniqueId } = storeToRefs(useConfigStore());
    const { selectedProductConfiguration, selectedProduct } = storeToRefs(useProductStore());

    const poleColor = computed(() => selectedProductConfiguration.value.poleColor);
    const filteredFields = computed(() =>
        selectedProductConfiguration.value.fields.filter(
            (filteredField) =>
                filteredField.transform.position.x + filteredField.transform.position.y !== 0 ||
                (filteredField as IImageField)?.url !== undefined ||
                (filteredField as ITextField)?.text !== undefined,
        ),
    );
    const { t } = useI18n();

    // For more information, see https://vitejs.dev/guide/build#load-error-handling
    function onVitePreloadError() {
        window.location.reload();
    }
    const customMaterial = ref<MeshStandardMaterial>(
        // metallic pole
        new MeshStandardMaterial({
            name: 'customMaterial',
            color: new Color(108 / 255, 108 / 255, 108 / 255),
            metalness: 0.5,
            roughness: 0.1,
        }),
    );
    const isMat = computed(() => selectedProductConfiguration.value.isMat);

    let updateCustomMaterial: (fields: ITextField[] | IImageField[], getImage: boolean) => Promise<void> = useMaterialUpdater(
        customMaterial,
        poleColor,
        fonts,
        1,
        uniqueId.value,
    );

    onMounted(() => {
        initValidators();
        window.addEventListener('vite:preloadError', onVitePreloadError);

        window.history.pushState(null, document.title, window.location.href);
        window.onpopstate = () => {
            const leavePage = window.confirm(t('popstate_alert'));
            if (leavePage) {
                window.onpopstate = null;
                window.history.back();
            } else {
                window.history.pushState(null, document.title, window.location.href);
            }
        };
    });

    onUnmounted(() => {
        window.removeEventListener('vite:preloadError', onVitePreloadError);
    });
    watch(uniqueId, (newUniqueId) => {
        updateCustomMaterial = useMaterialUpdater(customMaterial, poleColor, fonts, 1, newUniqueId);
        updateCustomMaterial(filteredFields.value, false);
    });
    watch([selectedProductConfiguration.value.fields, selectedProduct], () => {
        updateCustomMaterial(filteredFields.value, false);
    });
    const throttleColorChange = throttle(
        () => {
            updateCustomMaterial(filteredFields.value ?? [], false);
        },
        200,
        {
            throttleTimer: false,
        },
    );
    watch(poleColor, throttleColorChange);
    watch(isMat, (newMatValue) => {
        if (newMatValue) {
            customMaterial.value.metalness = 0;
            customMaterial.value.roughness = 0.7;
        } else {
            customMaterial.value.metalness = 0.5;
            customMaterial.value.roughness = 0.1;
        }
    });
</script>

<template>
    <TheLoader v-if="isAppLoading" />
    <MobileView
        v-if="isMobile"
        :update-custom-material
        :custom-material
    />
    <DesktopView
        v-else
        :update-custom-material
        :custom-material
    />
    <TheToastersManager />
</template>

<style>
    #app {
        position: absolute;
        width: 100%;
        height: 100%;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
    }
</style>
